import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import isRTL from '@adsk/bim360-matrix-react-helpers/es/isRTL';
import { ThemeConsumer, Themes } from '@adsk/bim360-matrix-react-helpers/es/theme';

import IconButton from '@adsk/bim360-matrix-react-icon-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import Loader from '@adsk/bim360-matrix-react-loader';
import infoCircle from '@adsk/bim360-matrix-react-assets/info-circle.svg';
import successIcon from '@adsk/bim360-matrix-react-assets/success-icon.svg';
import errorIcon from '@adsk/bim360-matrix-react-assets/error-icon.svg';
import warningIcon from '@adsk/bim360-matrix-react-assets/warning-icon.svg';
import alloyAlertInformation from '@adsk/bim360-matrix-react-assets/alloy-alert-information.svg';
import alloyAlertSuccess from '@adsk/bim360-matrix-react-assets/alloy-alert-success.svg';
import alloyAlertWarning from '@adsk/bim360-matrix-react-assets/alloy-alert-warning.svg';
import alloyAlertError from '@adsk/bim360-matrix-react-assets/alloy-alert-error.svg';
import alloyClose from '@adsk/bim360-matrix-react-assets/alloy-close.svg';

import closeIcon from '@adsk/bim360-matrix-react-assets/modal-close.svg';

import './_Toast.scss';

/* eslint-disable react/display-name */
const matrixIcons = {
  warning: () => <SvgIcon>{warningIcon}</SvgIcon>,
  error: () => <SvgIcon>{errorIcon}</SvgIcon>,
  info: () => <SvgIcon>{infoCircle}</SvgIcon>,
  success: () => <SvgIcon>{successIcon}</SvgIcon>
};
const alloyIcons = {
  warning: () => (
    <SvgIcon width="24px" height="24px">
      {alloyAlertWarning}
    </SvgIcon>
  ),
  error: () => (
    <SvgIcon width="24px" height="24px">
      {alloyAlertError}
    </SvgIcon>
  ),
  info: () => (
    <SvgIcon width="24px" height="24px">
      {alloyAlertInformation}
    </SvgIcon>
  ),
  success: () => (
    <SvgIcon width="24px" height="24px">
      {alloyAlertSuccess}
    </SvgIcon>
  )
};
/* eslint-enable react/display-name */

const getIcon = (level, theme) => {
  if (theme === Themes.ALLOY) {
    return alloyIcons[level];
  }
  return matrixIcons[level];
};

class Toast extends PureComponent {
  constructor(props) {
    super(props);

    const hasPromise = props.asyncOperation && typeof props.asyncOperation.then === 'function';
    this.state = {
      isLoading: hasPromise,
      asyncOperationResult: null
    };
    if (hasPromise) {
      // eslint-disable-next-line promise/catch-or-return
      props.asyncOperation
        .then(asyncOperationResult => {
          if (!this.unmounting) {
            this.setState({ asyncOperationResult, isLoading: false });
          }
          return asyncOperationResult;
        })
        .catch(e => {
          return e;
        });
    }

    this.renderCloseButton = this.renderCloseButton.bind(this);
  }

  componentWillUnmount() {
    this.unmounting = true;
  }

  renderCloseButton() {
    return (
      <ThemeConsumer>
        {({ theme }) => {
          const svgProps = {};
          if (theme === Themes.ALLOY) {
            svgProps.width = '24px';
            svgProps.height = '24px';
          }
          const icon = theme === Themes.ALLOY ? alloyClose : closeIcon;
          return (
            <IconButton
              className="Toast__close"
              data-testid="Toast__close-icon-button"
              onClick={() => this.props.onDismiss()}
            >
              <SvgIcon {...svgProps}>{icon}</SvgIcon>
            </IconButton>
          );
        }}
      </ThemeConsumer>
    );
  }

  render() {
    const message = this.props.messageRenderer
      ? this.props.messageRenderer({ asyncOperationResult: this.state.asyncOperationResult })
      : this.props.message;
    const isMessageElement = React.isValidElement(message);
    const contentClassNames = classNames('Toast__content', {
      'Toast__content--rtl': !isMessageElement && isRTL(message),
      'Toast__content--loading': this.state.isLoading
    });

    return (
      <ThemeConsumer>
        {({ theme }) => (
          <div
            className={classNames(
              'Toast__container',
              `Toast--${theme}`,
              `Toast__level--${this.props.level}`
            )}
          >
            <span
              className="Toast__level"
              data-testid={`Toast__level--${this.state.isLoading ? 'loading' : this.props.level}`}
            >
              {this.state.isLoading ? (
                <Loader className="Toast__loader" />
              ) : (
                getIcon(this.props.level, theme)()
              )}
            </span>
            <span className={contentClassNames}>
              {isMessageElement
                ? React.cloneElement(message, {
                  closeNotification: () => this.props.removeNotification()
                })
                : message}
            </span>
            {this.props.closeButtonRenderer({
              getCloseButton: this.renderCloseButton,
              asyncOperationResult: this.state.asyncOperationResult
            })}
          </div>
        )}
      </ThemeConsumer>
    );
  }
}

Toast.defaultProps = {
  closeButtonRenderer: ({ getCloseButton }) => getCloseButton()
};

Toast.propTypes = {
  /**
   * Notification level. See NotificationLevels const in NotificationManager.js
   */
  level: PropTypes.string.isRequired,
  /**
   * An Promise if the toast needs to be changed after rendered.
   * When the promise resolves it can return an object with `message`, `level` & closeButtonRenderer to update the toast state.
   */
  asyncOperation: PropTypes.shape({
    then: PropTypes.func.isRequired
  }),
  /**
   * Close button renderer. A function that gets as parameter an object containing `getCloseButton` & `asyncOperationResult`.
   * getCloseButton - the original close button renderer and can modify the close button if needed.
   * asyncOperationResult - the result of the async operation if provided.
   * It should return the close button to render.
   */
  closeButtonRenderer: PropTypes.func,
  /**
   * Message renderer. A function that gets as parameter an object containing `asyncOperationResult` - the result of the promise if provided.
   * It should return the message to render.
   */
  messageRenderer: PropTypes.func,
  /**
   * Toast message. Can be either a string or an element.
   */
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  /**
   * Remove notification callback.
   */
  removeNotification: PropTypes.func.isRequired,
  /**
   * Callback to be called when user dismisses the notification.
   */
  onDismiss: PropTypes.func
};

export default Toast;
